import ReconnectingWebSocket from 'reconnecting-websocket';
import { reaction } from 'mobx';
import { countdown } from './index';

// Client for the cf-backend (Cloudflare Worker + Durable Object) that drops the
// address-bound midnight claim token over a WebSocket, and serves the countdown
// time over HTTP. Replaces the token-drop + getTime parts of the old Node backend.

const HOST = 'isitalgorandsbirthday.com';

/** WebSocket base for cf-backend, env-switched the same way the old api.js did. */
export function dropWsBase() {
  if (import.meta.env.VITE_DROP_WS_URL) {
    return import.meta.env.VITE_DROP_WS_URL.replace(/\/+$/, '');
  }
  const { hostname } = window.location;
  if (hostname.endsWith(HOST)) {
    // production cf-backend (override with VITE_DROP_WS_URL once deployed)
    return `wss://drop.${hostname}`;
  }
  return `ws://${hostname}:8787`;
}

/** HTTP base for cf-backend (e.g. the /time endpoint), derived from the WS base. */
export function dropHttpBase() {
  return dropWsBase().replace(/^ws/, 'http');
}

/** GET cf-backend /time -> { time, bdayStart, bdayEnd } (seconds). */
export async function fetchServerTime() {
  const res = await fetch(`${dropHttpBase()}/time`);
  if (!res.ok) throw new Error(`time fetch failed: ${res.status}`);
  return res.json();
}

/**
 * GET cf-backend /nft/2026/tweet -> the URL the 2026 share-tweet quotes.
 * Returns '' when the worker has no URL configured (so the caller can omit the
 * quote line) or when the request fails.
 */
export async function fetchDaddyTweet() {
  try {
    const res = await fetch(`${dropHttpBase()}/nft/2026/tweet`);
    if (!res.ok) return '';
    const data = await res.json();
    return typeof data.daddyTweet === 'string' ? data.daddyTweet : '';
  } catch {
    return '';
  }
}

class DropClient {
  rws = null;
  address = null;
  turnstileToken = null;
  verified = false;
  discountToken = null;
  // Set by the TurnstileGate component: () => request a fresh widget token.
  onNeedToken = null;
  // Set by the buy page: (status) => 'pending' | 'verified' | 'failed'.
  onStatus = null;
  // Set by the app: (token) => called once when a brand-new drop token arrives,
  // so the UI can show a "token received" notification.
  onToken = null;
  // Latches true after the first token notification so we never show it twice,
  // even if the backend drops multiple (or repeated) tokens this session.
  tokenNotified = false;

  /** Wire the discount-token store the drop should write into. */
  init(discountTokenStore) {
    this.discountToken = discountTokenStore;
    // The drop is a one-shot midnight broadcast, so the socket is only useful in
    // the 3-hour run-up to it. React to the window opening/closing and connect or
    // tear down accordingly — the run-up may begin while a wallet is already
    // connected and the page is sitting idle.
    reaction(
      () => countdown.dropWindowOpen,
      () => this.ensureConnection(),
    );
  }

  /** Track the connected wallet address; registration is gated on Turnstile. */
  setAddress(address) {
    const next = address || null;
    if (next === this.address) return;
    this.address = next;
    this.verified = false;
    if (!this.address) return; // keep any existing socket; nothing to register
    if (!this.rws) this.ensureConnection();
    else this.tryRegister();
  }

  /**
   * Open the drop socket only when there's an address to register AND we're inside
   * the drop window; otherwise make sure no socket is left running. Driven by both
   * the wallet-address change and the countdown reaction in init().
   */
  ensureConnection() {
    const shouldConnect = !!this.address && countdown.dropWindowOpen;
    if (shouldConnect && !this.rws) this.open();
    else if (!shouldConnect && this.rws) this.close();
  }

  /** Feed a Turnstile token from the widget; triggers registration when ready. */
  setTurnstileToken(token) {
    this.turnstileToken = token || null;
    this.tryRegister();
  }

  /**
   * Register the address once we have an open socket + a Turnstile token. The
   * server verifies the token (siteverify) before the address becomes eligible
   * for the drop. If a token is missing, ask the widget for a fresh one.
   */
  tryRegister() {
    if (!this.rws || this.rws.readyState !== 1) return; // not open yet
    if (!this.address || this.verified) return;
    if (!this.turnstileToken) {
      if (this.onNeedToken) this.onNeedToken();
      return;
    }
    if (this.onStatus) this.onStatus('pending');
    this.send({ cmd: 'register', address: this.address, token: this.turnstileToken });
  }

  open() {
    // URL provider is re-read on every (re)connect, so it always uses the latest address.
    const urlProvider = () =>
      `${dropWsBase()}/ws${this.address ? `?address=${encodeURIComponent(this.address)}` : ''}`;
    this.rws = new ReconnectingWebSocket(urlProvider);
    this.rws.addEventListener('open', () => {
      // A reconnect is a brand-new (unverified) socket: re-register.
      this.verified = false;
      this.tryRegister();
    });
    this.rws.addEventListener('close', () => {
      this.verified = false;
      // Tokens are single-use/short-lived: never resend one across a reconnect.
      // Drop it so the next 'open' asks the widget for a fresh token instead.
      this.turnstileToken = null;
    });
    this.rws.addEventListener('message', this.onMessage);
    this.rws.addEventListener('error', (e) => console.log('dropClient error', e));
  }

  /** Close and discard the socket (e.g. once the drop window has passed). */
  close() {
    if (!this.rws) return;
    this.verified = false;
    this.turnstileToken = null;
    try {
      this.rws.close();
    } catch (e) {
      console.log('dropClient close error', e);
    }
    this.rws = null;
  }

  send(obj) {
    try {
      this.rws?.send(JSON.stringify(obj));
    } catch (e) {
      console.log('dropClient send error', e);
    }
  }

  onMessage = (msg) => {
    try {
      const data = JSON.parse(msg.data);
      switch (data.cmd) {
        case 'set-discount-token':
          if (data.token && this.discountToken && this.discountToken.token !== data.token) {
            // A token already present (e.g. persisted across a reload) means this
            // is a re-drop, not a first receipt — stay silent, like the old backend.
            const hadToken = !!this.discountToken.token;
            this.discountToken.setToken(data.token);
            // Notify exactly once per session, no matter how many tokens arrive.
            if (!hadToken && !this.tokenNotified) {
              this.tokenNotified = true;
              if (this.onToken) this.onToken(data.token);
            }
          }
          break;
        case 'registered':
          this.verified = !!data.verified;
          this.turnstileToken = null; // single-use; consumed by the server
          if (this.onStatus) this.onStatus(this.verified ? 'verified' : 'failed');
          break;
        case 'error':
          if (data.code === 'TURNSTILE') {
            // token invalid/expired — drop it and ask the widget for a new one.
            this.turnstileToken = null;
            if (this.onStatus) this.onStatus('failed');
            if (this.onNeedToken) this.onNeedToken();
          }
          break;
        default:
          break; // 'connected' / 'pong' are informational
      }
    } catch (e) {
      console.log('dropClient parse error', e);
    }
  };
}

const dropClient = new DropClient();
export default dropClient;
